import {
  Button, Divider, FormControl, FormHelperText, Grid, InputLabel, TextField, 
  Typography, withStyles, WithStyles
} from '@material-ui/core';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import React, { Fragment, useState } from 'react';
import { withLocale } from '../../hocs/withLocale';
import { compose } from 'recompose';
import { serviceLocator } from '../../infrastructure/ServiceLocator';
import LayoutHelper from '../../infrastructure/LayoutHelper';
import { SignUpConfirmationButtons } from './SignUpConfirmationButtons';
import { withFormik, FormikProps, FormikErrors } from 'formik';
import { BookingTermsAndConditions } from './BookingTermsAndConditions';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import { Recaptcha } from '../../components/Shared/Recaptcha';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import MuiPhoneInput from '../../components/Form/MuiPhoneInput';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Theme, createStyles } from '@material-ui/core/styles';
import { ClassInfo, Owner, Studio, Payment, StudioClient, Course } from './SignUpConfirmationButtons';

interface FormFieldStyles {
  field: string;
  label: string;
}

interface FormFieldProps extends WithStyles<typeof formFieldStyles> {
  label: React.ReactNode;
  name: string;
  errors: FormikErrors<FormValues>;
  children: React.ReactNode;
}

const formFieldStyles = (theme: Theme) => createStyles({
  field: {
    display: 'flex',
    flexDirection: 'column',
    margin: `${theme.spacing(2)}px 0`,
  },
  label: {
    marginBottom: theme.spacing(1),
    position: 'relative',
    transform: 'none',
    display: 'flex',
    alignItems: 'center',
  },
});

const FormField = withStyles(formFieldStyles)(({ label, name, errors, children, classes }: FormFieldProps) => {
  const error = errors[name];
  return (
    <FormControl className={classes.field} error={!!error}>
      <InputLabel className={classes.label}>
        {label}
        {error && <FormHelperText>&nbsp;{error}</FormHelperText>}
      </InputLabel>
      {children}
    </FormControl>
  );
});

interface GuestBookingFormStyles {
  phoneInput: string;
  containerWidth: string;
  confirmed: string;
  buttons: string;
}

const guestBookingFormStyles = (theme: Theme) => createStyles({
  phoneInput: {
    marginTop: theme.spacing(1.5),
  },
  containerWidth: { width: 'auto' },
  confirmed: {
    height: theme.spacing(10),
    color: 'green',
    display: 'flex',
    alignItems: 'center',
  },
  buttons: {
    [theme.breakpoints.down('xs')]: {
      position: 'fixed',
      left: 0,
      bottom: 0,
      width: '100%',
    },
  },
});

interface FormValues {
  domain: {
    id: string;
    countryCode: string;
  };
  phone?: string;
  clientName?: string;
  clientEmail?: string;
  clientNote?: string;
  studioClient?: StudioClient;
  tcAccepted?: boolean;
  captcha?: boolean;
  errors?: string[];
}

interface GuestBookingForm2Props extends WithStyles<typeof guestBookingFormStyles>, WithSnackbarProps {
  owner: Owner;
  studio: Studio;
  classInfo: ClassInfo;
  setAttendance: (attendance: any) => void;
  onSuccess: (attendance: any) => void;
  onFailure: (error: any) => void;
  translate: (key: string, data?: any) => string;
  domain: {
    domain: {
      id: string;
      countryCode: string;
    };
  };
  course?: Course;
}

interface GuestBookingForm2EnhancedProps extends GuestBookingForm2Props, FormikProps<FormValues> {}

export const GuestBookingForm2 = compose<GuestBookingForm2EnhancedProps, GuestBookingForm2Props>(
  withLocale,
  withSnackbar,
  withStyles(guestBookingFormStyles),
  withFormik<GuestBookingForm2Props, FormValues>({
    mapPropsToValues: (props) => ({ domain: props.domain.domain }),
    validate: (values, props) => {
      let errors: FormikErrors<FormValues> = {};

      if (
        values.clientEmail &&
        !LayoutHelper.isEmailValid(values.clientEmail)
      ) {
        errors.clientEmail = props.translate('common.invalidEmail');
      }
      if (values.phone && !isValidPhoneNumber(values.phone)) {
        errors.phone = props.translate('common.invalidPhone');
      }

      let mandatoryFields: (keyof FormValues)[] = ['phone', 'clientName'];
      if (!values.studioClient) {
        mandatoryFields.push('tcAccepted');
        mandatoryFields.push('captcha');
      }
      mandatoryFields.forEach((field) => {
        if (!values[field]) {
          errors[field] = '*';
        }
      });
      return errors;
    },
    displayName: 'BookingClassInfoForm',
  })
)(
  ({
    owner,
    studio,
    classInfo,
    setAttendance,
    onSuccess,
    onFailure,
    values,
    errors,
    handleChange,
    isSubmitting,
    setSubmitting,
    translate,
    classes,
    enqueueSnackbar,
    dirty,
    course,
  }) => {
    const [estimatingAttendance, setEstimatingAttendance] = useState<boolean>(false);
    const [validatingClient, setValidatingClient] = useState<boolean>(false);
    const [hasRatePlan, setHasRatePlan] = useState<boolean>(false);

    const getClient = (phone: string) => {
      if (!(phone && isValidPhoneNumber(phone))) return;
      setValidatingClient(true);
      serviceLocator.onlineRegistrationsApiCall
        .studioClientsByPhone(studio.id, phone, classInfo.groupId)
        .then((data: { attendance: any; client: StudioClient }) => {
          const { attendance, client } = data;
          handleChange('studioClient')(client);
          //setHasRatePlan(false);
          if (client) {
            ///estimateAttendance(client, classInfo)
            handleChange('clientName')(client.name);
            handleChange('clientEmail')(client.email);
            setValidatingClient(false);
          } else {
            handleChange('clientName')('');
            handleChange('clientEmail')('');
            setValidatingClient(false);
          }
          if (attendance) {
            setAttendance(attendance);
          }
          //resetClient(client ? client : {name: '', email: ''});
        }, LayoutHelper.onErr);
    };
    
    const estimateAttendance = async (client: StudioClient, classInfo: ClassInfo) => {
      setEstimatingAttendance(true);
      const api = serviceLocator.registrationApi;
      api
        .estimateAttendance(classInfo.studioId, classInfo, client)
        .then((data: { payment: Payment; attendance: any }) => {
          //console.log(data)
          setEstimatingAttendance(false);
          let payment = data.payment;
          let attendance = data.attendance;

          if (payment && payment.clientRatePlan) {
            setHasRatePlan(true);
          }
          handleChange('clientName')(client.name);
          handleChange('clientEmail')(client.email);
          setValidatingClient(false);
        }, LayoutHelper.onErr);
    };
    
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;
      if (value) {
        value = value.trim();
      }
      handleChange('clientEmail')(value);
    };

    const signUp = async (payment?: Payment) => {
      setSubmitting(true);
      try {
        const attendance = await serviceLocator.registrationApi.noPinSignUp(
          studio.id,
          classInfo.groupId,
          {
            domain: values.domain.id,
            phone: values.phone,
            email: values.clientEmail,
            name: values.clientName,
            note: values.clientNote,
          },
          payment,
          classInfo.attendancePrice
        );
        onSuccess(attendance);
      } catch (e) {
        onFailure(e);
      }
      setSubmitting(false);
    };
    
    const onPaymentFailed = async () => {
      enqueueSnackbar("Can't process the payment", {
        persist: true,
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
      setSubmitting(false);
    };

    if (values.errors) {
      return <>{values.errors.map((e, index) => <SnackbarContent key={index} message={e} />)}</>;
    }
    
    return (
      <div>
        <Grid container direction="column">
          <FormField
            label={
              <Fragment>{translate('manageRegistration.clientPhone')}</Fragment>
            }
            errors={errors}
            name="phone"
          >
            <MuiPhoneInput
              className={classes.phoneInput}
              defaultCountry={values.domain.countryCode}
              value={values.phone}
              id="phone"
              onChange={(phone: string) => {
                handleChange('phone')(phone);
                return getClient(phone);
              }}
            />
          </FormField>
          {validatingClient && (
            <Typography>
              <CircularProgress style={{ width: '16px', height: '16px' }} />{' '}
              Validating your phone
            </Typography>
          )}
          <Typography align="center" variant="h6">
            {translate('myClients.clientInfo')}
          </Typography>

          <FormField
            label={<b>{translate('manageRegistration.clientName')}*</b>}
            errors={errors}
            name="clientName"
          >
            <TextField
              id="clientName"
              value={values.clientName}
              variant="outlined"
              onChange={(e) => {
                handleChange('clientName')(e.target.value);
              }}
              margin="normal"
            />
          </FormField>
          <FormField
            label={translate('manageRegistration.clientEmail')}
            errors={errors}
            name="clientEmail"
          >
            <TextField
              id="clientEmail"
              value={values.clientEmail}
              variant="outlined"
              onChange={handleEmailChange}
              margin="clientEmail"
            />
          </FormField>
          {studio.requestFieldFromGuest && (
            <FormField
              label={studio.requestFieldFromGuest}
              errors={errors}
              name="clientNote"
            >
              <TextField
                id="clientNote"
                variant="outlined"
                value={values.clientNote}
                onChange={(e) => handleChange(e)}
                margin="clientNote"
              />
            </FormField>
          )}
          {!values.studioClient && (
            <Fragment>
              <BookingTermsAndConditions
                accepted={values.tcAccepted}
                onChange={(a) => {
                  handleChange('tcAccepted')(a);
                }}
                owner={owner}
              />
              <div style={{ marginBottom: '10px' }}>
                <Recaptcha
                  onVerify={(canSubmit) => handleChange('captcha')(canSubmit)}
                />
              </div>
            </Fragment>
          )}

          <div className={classes.buttons}>
            <SignUpConfirmationButtons
              disabled={
                isSubmitting ||
                Object.keys(errors).length > 0 ||
                !dirty ||
                validatingClient
              }
              owner={owner}
              showLoader={isSubmitting}
              clientData={values}
              studioClient={values.studioClient}
              phone={values.phone}
              course={course}
              classInfo={classInfo}
              onSignUp={signUp}
              studio={studio}
              onPaymentFailed={onPaymentFailed}
              domain={{ domain: values.domain }}
            />
          </div>
        </Grid>
      </div>
    );
  }
);